import { useCallback, useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { Accordion, Label } from '@trussworks/react-uswds';
import { mdiCloudUploadOutline, mdiFileDocumentOutline } from '@mdi/js';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@src/api/queryClient';
import { FILE_STORAGE_QUERY_KEYS, FileStorageMutations, FileStorageQueries } from '@api/services/file-storage';

import LinkButton from '@components/link/linkButton';
import TextArea from '@components/textarea/TextArea';
import FileInput from '@components/file-input/FileInput';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import Icon from '@components/icon/Icon';
import LoaderButton from '@components/loader/LoaderButton';
import { ModalFooter } from '@components/modal';
import LabelValueComponent from '@components/label-value/LabelValue';
import { ADDITIONAL_INFO_QUERY_KEYS, AdditionalInfoMutations } from '@api/services/additional-info';
import { StorageReasons, ResponseStatuses } from '@src/utils/enums';
import { useModal } from '@stores';
import statusModal from '@pages/Forms/components/modals/statusModal';
import { formatBytes } from '@src/utils/helpers';
import { DASHBOARD_QUERY_KEYS } from '@api/services/dashboard';
import { parseApiValidationErrors } from '@src/api/helpers';

const formatPMName = (firstName, lastName) => {
  if (!firstName || !lastName) return null;
  return `${firstName} ${lastName}`;
};

const LabelValue = ({ label, value, isDate }) => {
  const displayValue = isDate && value ? value.split(' ')[0] : value;
  return <LabelValueComponent label={label} value={displayValue} />;
};

const schema = yup.object().shape({
  commentResponse: yup.string().required('Field is required'),
});

const AdditionalInfoRequest = ({ data, onClose, isReadOnly, isSaving }) => {
  const additionalInformationRequest = data?.additionalInformationRequest;
  const additionalInformationResponse = data?.additionalInformationResponse;
  const [stagedFiles, setStagedFiles] = useState([]);
  const [fileErrors, setFileErrors] = useState(false);
  const [apiErrors, setApiErrors] = useState([]);
  const [saveClicked, setSaveClicked] = useState(false);
  const { closeModal, openSecondaryModal } = useModal();

  const defaultValues = {
    additionalInformationResponseID: additionalInformationResponse?.additionalInformationResponseID ?? null,
    projectID: additionalInformationRequest?.projectID ?? '',
    requestID: additionalInformationRequest?.requestID ?? '',
    version: additionalInformationRequest?.version ?? '',
    ormAdditionalInfoActionID: additionalInformationRequest?.ormAdditionalInfoActionID ?? '',
    ormActionType: additionalInformationRequest?.actionType ?? '',
    commentResponse: additionalInformationResponse?.commentResponse ?? '',
    files: additionalInformationResponse?.files ?? [],
  };

  const methods = useForm({ resolver: yupResolver(schema), mode: 'onBlur', defaultValues: defaultValues });
  const {
    formState: { errors, isValid },
    watch,
    setFocus,
    trigger,
    getValues,
    setValue,
  } = methods;
  const files = watch('files');
  const ormAdditionalInfoActionID = watch('ormAdditionalInfoActionID');
  const projectID = watch('projectID');
  const requestID = watch('requestID');
  const version = watch('version');
  const scope = 'AdditionalInfoResponseFolder';

  const isS3File = stagedFiles?.filter((item) => item.isUploadedToS3).length > 0;

  const { data: userFileDetails } = useQuery({
    queryKey: [FILE_STORAGE_QUERY_KEYS.GET_USER_FILE_DETAILS, ormAdditionalInfoActionID],
    queryFn: () =>
      FileStorageQueries.getUserFilesDetails({ projectID, requestID, version, ormAdditionalInfoActionID, scope }),
  });

  // Save/Submit Response comment
  const { mutate: saveAdditionalInfoComments } = useMutation({
    mutationFn: AdditionalInfoMutations.saveAdditionalInfoResponse,
    onSuccess: (data, variables) => {
      const { isSubmit } = variables;
      if (data.status === ResponseStatuses.Success) {
        // Pull latest AIR Dashboard data
        queryClient.invalidateQueries({
          queryKey: [ADDITIONAL_INFO_QUERY_KEYS.GET_ALL_REQUESTS],
        });
        // Pull latest Request Dashboard data
        queryClient.invalidateQueries({
          queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD],
        });
        // Ensure uploaded file list refreshes after submit
        queryClient.invalidateQueries({
          queryKey: [FILE_STORAGE_QUERY_KEYS.GET_USER_FILE_DETAILS, ormAdditionalInfoActionID],
        });

        //Update modal data to get additionalInformationResponse.additionalInformationResponseID
        queryClient.invalidateQueries({
          queryKey: [
            ADDITIONAL_INFO_QUERY_KEYS.GET_ADDITIONAL_INFO_FULL,
            ormAdditionalInfoActionID,
            requestID,
            version,
          ],
        });
      }
      if (isSubmit && data.status === ResponseStatuses.Success) {
        closeModal();
        openSecondaryModal(statusModal, {
          msg: data?.msg,
          status: data.status ?? ResponseStatuses.Success,
        });
      } else if (data.status !== ResponseStatuses.Success) {
        const errors = parseApiValidationErrors(data);
        setApiErrors(() => errors);
      } else if (!isSubmit && data.status === ResponseStatuses.Success) {
        toast.success(data?.msg);
        setApiErrors([]);
      }
    },
  });

  //Upload response attachments
  const { mutate: saveAttachments } = useMutation({
    mutationFn: FileStorageMutations.uploadAirAttachments,
    onSuccess: (data, variables) => {
      const { status } = variables;
      const isSubmit = status === 'Submit';

      setStagedFiles([]);
      const latestValues = getValues();

      var payload = latestValues;
      delete payload.files;
      delete payload.additionalInformationResponseID;
      if (isSubmit) {
        payload = { ...payload, isSubmit };
      }
      if (additionalInformationResponse?.additionalInformationResponseID) {
        payload = {
          ...payload,
          additionalInformationResponseID: additionalInformationResponse?.additionalInformationResponseID,
        };
      }
      saveAdditionalInfoComments(payload);
    },
    onError: () => {
      toast.error('Failed to save attachments.');
    },
  });

  //download file
  const { mutate: downloadFile } = useMutation({
    mutationFn: FileStorageMutations.downloadFile,
    onSuccess: (data, variables) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = variables.fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    },
    onError: () => {
      toast.error('Failed to download attachments.');
    },
  });

  const { mutateAsync: deleteFile } = useMutation({
    mutationFn: FileStorageMutations.deleteFile,
    onError: () => {
      toast.error('Failed to remove attachments.');
    },
  });

  //download ALL user response files that have been uploaded to S3
  const handleResponseFileDownload = useCallback(
    (file) => {
      const { fileName } = file;
      const { projectID, requestID, version, ormAdditionalInfoActionID } = getValues();
      const subFolder = `AdditionalInformationResponses/${ormAdditionalInfoActionID}`;
      downloadFile({
        fileName,
        projectID,
        requestID,
        version,
        reason: StorageReasons.Project,
        subFolder,
      });
    },
    [downloadFile, getValues]
  );

  //download file from request accordion
  const handleRequestFileDownload = useCallback(
    (file) => {
      const { fileName } = file;
      const { ormAdditionalInfoActionID } = getValues();
      downloadFile({ fileName, ormAdditionalInfoActionID, reason: StorageReasons.AirRequest });
    },
    [downloadFile, getValues]
  );

  const handleDownloadFiles = (files) => {
    const { projectID, requestID, version, ormAdditionalInfoActionID } = getValues();
    const subFolder = `AdditionalInformationResponses/${ormAdditionalInfoActionID}`;
    isS3File &&
      files.forEach((item) =>
        downloadFile({
          fileName: item?.fileName,
          projectID,
          requestID,
          version,
          reason: StorageReasons.Project,
          subFolder,
        })
      );
  };

  const handleDeleteFiles = (files) => {
    setValue('files', '', { shouldValidate: true });
    const { projectID, requestID, version, ormAdditionalInfoActionID } = getValues();
    const subFolder = `AdditionalInformationResponses/${ormAdditionalInfoActionID}`;
    if (isS3File) {
      const deleteAllFiles = (files) => {
        return Promise.all(
          files.map((item) =>
            deleteFile({
              fileName: item?.fileName,
              projectID,
              requestID,
              version,
              subFolder,
            })
          )
        );
      };
      deleteAllFiles(files)
        .then(() => {
          toast.success('All files have been successfully removed.');
          queryClient.invalidateQueries({
            queryKey: [FILE_STORAGE_QUERY_KEYS.GET_USER_FILE_DETAILS, ormAdditionalInfoActionID],
          });
        })
        .catch(() => {
          toast.error('There was an error removing one or more files.');
          queryClient.invalidateQueries({
            queryKey: [FILE_STORAGE_QUERY_KEYS.GET_USER_FILE_DETAILS, ormAdditionalInfoActionID],
          });
        });
    }
  };

  const renderFiles = useCallback(
    (files, isUserUploadedFiles) => {
      return files?.map((file, index) => {
        const fileSize = formatBytes(file.fileSize);
        const fileType = `${file?.fileName?.toUpperCase().split('.').pop()}`;
        return (
          <div key={index} className='additional-info-file'>
            <Icon key={`icon_${index}`} focusable={false} path={mdiFileDocumentOutline} size={1} />
            <LinkButton
              key={`link_${index}`}
              href={''}
              rel='noreferrer'
              tabIndex={0}
              target='_blank'
              title={file.fileName}
              onClick={
                isUserUploadedFiles ? () => handleResponseFileDownload(file) : () => handleRequestFileDownload(file)
              }
            >
              <span className='text-bold'>{`${file.fileName} [${fileType}, ${fileSize}]`}</span>
            </LinkButton>
          </div>
        );
      });
    },
    [handleRequestFileDownload, handleResponseFileDownload]
  );

  //define accordions
  const requestFilesAccordion = useMemo(
    () => [
      {
        title: `Attachments (${additionalInformationRequest?.files?.length})`,
        content: renderFiles(additionalInformationRequest?.files),
        id: '1',
        headingLevel: 'p',
        expanded: true,
      },
    ],
    [additionalInformationRequest?.files, renderFiles]
  );

  const requestInfoAccordion = useMemo(
    () => [
      {
        title: 'Requested Information',
        content: (
          <div className='usa-prose' style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {additionalInformationRequest?.questions ?? 'No Information Available'}
          </div>
        ),
        id: '2',
        headingLevel: 'p',
        expanded: true,
      },
    ],
    [additionalInformationRequest?.questions]
  );

  const handleFileInput = (e) => {
    const filesArr = Array.from(e?.target?.files) ?? [];
    setStagedFiles(filesArr);
    trigger('files');
  };

  const handleSave = useCallback(
    (isSubmit) => {
      setSaveClicked(true);
      const latestValues = getValues();

      var payload = latestValues;
      delete payload.files;
      delete payload.additionalInformationResponseID;

      //if staged files, upload to S3 then save/submit response else save/submit response.
      if (!isS3File && stagedFiles?.length > 0) {
        saveAttachments({
          projectID: latestValues?.projectID,
          requestID: latestValues?.requestID,
          version: latestValues?.version,
          ormAdditionalInfoActionID: latestValues?.ormAdditionalInfoActionID,
          fileData: stagedFiles,
          status: isSubmit ? 'Submit' : 'Draft',
        });
      } else {
        if (isSubmit) {
          payload = { ...payload, isSubmit };
        }
        if (additionalInformationResponse?.additionalInformationResponseID) {
          payload = {
            ...payload,
            additionalInformationResponseID: additionalInformationResponse?.additionalInformationResponseID,
          };
        }
        saveAdditionalInfoComments(payload);
      }
    },
    [
      getValues,
      saveAttachments,
      additionalInformationResponse?.additionalInformationResponseID,
      isS3File,
      stagedFiles,
      saveAdditionalInfoComments,
    ]
  );

  const setFileValue = useCallback(() => {
    if (files?.length === 0) {
      return null;
    }
    return fileErrors === true ? 'error' : 'true';
  }, [files?.length, fileErrors]);

  // Set files value
  useEffect(() => {
    setValue('files', setFileValue(), { shouldValidate: true });
  }, [fileErrors, setValue, setFileValue]);

  useEffect(() => {
    setFocus(errors?.[Object.keys(errors)[0]]?.['ref']?.['id']);
  }, [errors, setFocus]);

  // Update staged files state with latest S3 files
  useEffect(() => {
    if (userFileDetails) {
      setStagedFiles(userFileDetails);
    }
  }, [userFileDetails]);

  return (
    <>
      <section className='modal-body'>
        <div className='additional-info-container'>
          {!isReadOnly && errors && <ErrorSummary errors={errors} modalID='additionalInfoRequestModal' type='modal' />}
          {!isReadOnly && apiErrors.length > 0 && <ErrorSummary errors={apiErrors} type='api-validation' />}
          <section className='additional-info-section flex-item'>
            <h3>Details</h3>
            <div className='additional-info-flex-container'>
              <div className='additional-info-flex-item'>
                <LabelValue label='Project Name' value={additionalInformationRequest?.projectName} />
                <LabelValue label='Request Type' value={additionalInformationRequest?.requestType} />
                <LabelValue
                  label='Project Manager'
                  value={formatPMName(
                    additionalInformationRequest?.pmInfo?.firstName,
                    additionalInformationRequest?.pmInfo?.lastName
                  )}
                />
                <LabelValue label='Project Manager Contact' value={additionalInformationRequest?.pmInfo?.email} />
              </div>
              <div className='additional-info-flex-item margin-bottom-1'>
                <LabelValue label='Request Status' value={additionalInformationRequest?.requestStatus} />
                <LabelValue
                  label='Date Information Requested'
                  value={additionalInformationRequest?.dateRequested}
                  isDate
                />
                <LabelValue label='Response Due Date' value={additionalInformationRequest?.dueDate} isDate />
              </div>
            </div>
            <Accordion items={requestInfoAccordion} />
            {additionalInformationRequest?.files?.length > 0 && <Accordion items={requestFilesAccordion} />}
          </section>
          <section className='additional-info-section flex-item'>
            <h3>{`${!isReadOnly ? 'Respond to Additional Information Request' : 'Additional Information Request Response'}`}</h3>
            <FormProvider {...methods}>
              {!isReadOnly && (
                <div className='additional-info-button-container'>
                  <LoaderButton
                    className='action-button button-small save-progress'
                    onClick={() => handleSave(false)}
                    isLoading={isSaving && saveClicked}
                  >
                    <Icon path={mdiCloudUploadOutline} focusable={false} size={1} />
                    Save Progress
                  </LoaderButton>
                </div>
              )}
              <TextArea name='commentResponse' label='Response' required readOnly={isReadOnly} />
              {!isReadOnly && (
                <Label className='padding-bottom-0 margin-top-2' htmlFor='files'>
                  <span id='files_label'>Files</span>
                </Label>
              )}
              {(!isReadOnly || (isReadOnly && userFileDetails?.length > 0)) && (
                <FileInput
                  deleteFileCallback={() => handleDeleteFiles(stagedFiles)}
                  downloadFileCallback={() => handleDownloadFiles(stagedFiles)}
                  id='files'
                  multiple={true}
                  name='files'
                  onChangeCallback={handleFileInput}
                  readOnly={isS3File || isReadOnly}
                  setFileErrors={setFileErrors}
                  showDownloadButton={isS3File}
                  showDeleteButton={isS3File && !isReadOnly}
                  value={stagedFiles}
                  scope={StorageReasons.AirRequest}
                />
              )}
            </FormProvider>
          </section>
        </div>
      </section>
      <ModalFooter
        onCancel={isReadOnly ? null : onClose}
        onSave={isReadOnly ? onClose : () => handleSave(true)}
        saveIsDisabled={!isValid && !isReadOnly}
        saveText={isReadOnly ? 'Close' : 'Submit Response'}
        showCancelButton={!isReadOnly}
        customClosingLogic={!isReadOnly}
      />
    </>
  );
};

export default AdditionalInfoRequest;
